import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star } from "lucide-react";
import categories from "../Data/Categories";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { cn } from "../lib/utils";

const InfoCatalogo = ({
  empresas,
  page,
  totalPages,
  setPage,
  onContact,
  onNextPage,
  onPreviousPage,
  clearFilters,
  favoritosIds,
  onToggleFavorito,
}) => {
  const [localEmpresas, setLocalEmpresas] = useState(empresas);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [starsState, setStarsState] = useState({}); // 🔹 Estado por empresa para estrellas
  const navigate = useNavigate();

  const starColors = ["#d62246", "#f97316", "#eab308", "#4bbf73", "#2A9D8F"];

  useEffect(() => {
    setLocalEmpresas(empresas);
  }, [empresas]);

  const handleNextPage = () => {
    onNextPage(); // Llama a la función pasada por props
  };

  const handlePreviousPage = () => {
    onPreviousPage(); // Llama a la función pasada por props
  };

  const renderPaginationButtons = () => {
    const pages = [];
    if (page > 1) {
      pages.push(page - 1);
    }
    pages.push(page);
    if (page < totalPages) {
      pages.push(page + 1);
    }
    return pages;
  };

  return (
    <div>
      {localEmpresas.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-2">
          {localEmpresas.map((empresa) => {
            const selectedStars = starsState[empresa.id_company]?.selected || 0;
            const hoveredStars = starsState[empresa.id_company]?.hovered || 0;

            const setHoveredStars = (stars) =>
              setStarsState((prev) => ({
                ...prev,
                [empresa.id_company]: {
                  ...prev[empresa.id_company],
                  hovered: stars,
                },
              }));

            const setSelectedStars = (stars) =>
              setStarsState((prev) => ({
                ...prev,
                [empresa.id_company]: {
                  ...prev[empresa.id_company],
                  selected: stars,
                },
              }));

            return (
              <Card
                key={empresa.id_company}
                className={cn(
                  selectedEmpresa?.id_company === empresa.id_company && "ring-2 ring-primary"
                )}
              >
                <CardContent className="pt-6">
                  <h4 className="flex items-center gap-2">
                    <Link
                      className="text-lg font-semibold text-foreground hover:text-primary"
                      to={`/company-profile/${empresa.id_company}/${empresa.nombre_empresa}`}
                    >
                      {empresa.nombre_empresa}
                    </Link>
                    {empresa.certificado && <Badge>Certificado</Badge>}
                  </h4>

                  <div className="mt-3 flex gap-4">
                    <div className="shrink-0">
                      {/* Encuentra la categoría correspondiente */}
                      {categories.map((cat) =>
                        empresa.sector?.includes(cat.label) ? (
                          <div key={cat.label} className="flex flex-col items-center gap-1 text-center">
                            <span className="text-2xl">{cat.icon}</span>
                            <span className="text-xs text-muted-foreground">{empresa.sector}</span>
                          </div>
                        ) : null
                      )}

                      {/* Si no hay coincidencia, muestra el ícono predeterminado */}
                      {!categories.some((cat) => empresa.sector?.includes(cat.label)) && (
                        <div className="flex flex-col items-center gap-1 text-center">
                          <span className="text-2xl">⚙️</span>
                          <span className="text-xs text-muted-foreground">{empresa.sector}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 space-y-2 text-sm">
                      {/* ⭐ Mostrar puntaje de la empresa */}
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {Array(5)
                            .fill(0)
                            .map((_, index) => {
                              const score = empresa?.puntaje_promedio?.promedio || 0;
                              const roundedScore = Math.round(score * 2) / 2; // Redondea al medio más cercano (0.5 pasos)
                              const isFull = index + 1 <= Math.floor(roundedScore); // Estrella completamente llena
                              const isHalf = index + 1 === Math.ceil(roundedScore) && !isFull; // Media estrella

                              const colorIndex = Math.min(Math.ceil(score), starColors.length) - 1;

                              return (
                                <span
                                  key={index}
                                  className="flex h-5 w-5 items-center justify-center rounded-full text-xs"
                                  style={{
                                    background: isFull
                                      ? starColors[colorIndex] // Color completo
                                      : isHalf
                                      ? `linear-gradient(90deg, ${starColors[colorIndex]} 50%, #ccc 50%)` // Media estrella
                                      : "#ccc",
                                    color: "white",
                                  }}
                                >
                                  ★
                                </span>
                              );
                            })}
                        </div>
                        <span className="text-muted-foreground">
                          {empresa?.puntaje_promedio?.promedio
                            ? empresa.puntaje_promedio.promedio.toFixed(1)
                            : "0.0"}{" "}
                          ({empresa?.puntaje_promedio?.num_reviews || 0} reseñas)
                        </span>
                      </div>

                      <p className="text-muted-foreground">
                        <strong className="text-foreground">Descripción:</strong>{" "}
                        {empresa.descripcion
                          ? `${empresa.descripcion.slice(0, 200)}${
                              empresa.descripcion.length > 200 ? "..." : ""
                            }`
                          : "Sin descripción"}
                      </p>

                      <p className="text-muted-foreground">
                        <strong className="text-foreground">Página Web:</strong>{" "}
                        {empresa.pagina_web
                          ? (() => {
                              let url = empresa.pagina_web.trim(); // ✅ Eliminar espacios en blanco

                              // ✅ Agregar "https://" si no lo tiene
                              if (!url.startsWith("http://") && !url.startsWith("https://")) {
                                url = `https://${url}`;
                              }

                              // ✅ Agregar "www." si no lo tiene y si es un dominio de segundo nivel
                              try {
                                const urlObj = new URL(url);
                                if (
                                  !urlObj.hostname.startsWith("www.") &&
                                  urlObj.hostname.split(".").length === 2
                                ) {
                                  url = url.replace("//", "//www.");
                                }
                              } catch (error) {
                                console.error("URL inválida:", empresa.pagina_web);
                              }

                              return (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline"
                                >
                                  {empresa.pagina_web}
                                </a>
                              );
                            })()
                          : "No disponible"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(`/company-profile/${empresa.id_company}/${empresa.nombre_empresa}`)
                        }
                      >
                        Ver Perfil
                      </Button>
                      {onToggleFavorito && (
                        <button
                          type="button"
                          title={
                            favoritosIds?.has(empresa.id_company)
                              ? "Quitar de favoritos"
                              : "Guardar como favorito"
                          }
                          onClick={() => onToggleFavorito(empresa)}
                          className="flex h-9 w-9 items-center justify-center rounded-md border border-border hover:bg-muted"
                        >
                          <Star
                            className={cn(
                              "h-4 w-4",
                              favoritosIds?.has(empresa.id_company)
                                ? "fill-primary text-primary"
                                : "text-muted-foreground"
                            )}
                          />
                        </button>
                      )}
                    </div>

                    {/* ⭐ Evaluación de empresa */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        <strong>Evalúanos:</strong>
                      </span>
                      <div className="flex">
                        {Array(5)
                          .fill(0)
                          .map((_, index) => (
                            <button
                              key={index}
                              type="button"
                              className="flex h-5 w-5 items-center justify-center rounded-full text-xs"
                              style={{
                                backgroundColor:
                                  hoveredStars >= index + 1 || selectedStars >= index + 1
                                    ? starColors[selectedStars || hoveredStars - 1] || "#ccc"
                                    : "#ccc",
                                color: "#fff",
                              }}
                              onMouseEnter={() => setHoveredStars(index + 1)}
                              onMouseLeave={() => setHoveredStars(0)}
                              onClick={() => {
                                setSelectedStars(index + 1);
                                navigate("/reseñas", {
                                  state: {
                                    score: index + 1,
                                    reviewTarget: {
                                      id_empresa: empresa.id_company,
                                      nombre_empresa: empresa.nombre_empresa,
                                    },
                                  },
                                });
                              }}
                            >
                              ★
                            </button>
                          ))}
                      </div>
                    </div>

                    <Button size="sm" onClick={() => onContact(empresa)}>
                      Contactar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <p className="text-muted-foreground">No hay empresas disponibles con estos filtros.</p>
          <Button variant="outline" className="mt-4" onClick={clearFilters}>
            Limpiar Filtros
          </Button>
        </div>
      )}

      <div className="mt-8 flex items-center justify-center gap-2">
        <Button variant="outline" size="sm" onClick={handlePreviousPage} disabled={page === 1}>
          Atrás
        </Button>
        {renderPaginationButtons().map((p, index) => (
          <Button
            key={index}
            variant={p === page ? "default" : "outline"}
            size="sm"
            onClick={() => setPage(p)}
          >
            {p}
          </Button>
        ))}
        <Button variant="outline" size="sm" onClick={handleNextPage} disabled={page === totalPages}>
          Siguiente
        </Button>
      </div>
    </div>
  );
};

export default InfoCatalogo;
